import { Tooltip, Box, Typography, useTheme, alpha } from "@mui/material";
import { useMemo } from "react";
import { useIsFeatureFlagEnabled } from "../../../../featureFlags/useIsFeatureFlagEnabled";

type TonnageSummaryProps = {
  doors?: number;
  loadedBills: number;
  notLoadedBills: number;
  loadedPallets: number;
  notLoadedPallets: number;
  loadedPieces: number;
  notLoadedPieces: number;
  loadedHandlingUnits: number;
  notLoadedHandlingUnits: number;
  loadedWeight: number;
  totalWeight: number;
};

interface DotWithLabelProps {
  color?: string;
  label: string;
}

export const DotWithLabel = ({ color = "gray", label }: DotWithLabelProps) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginRight: "16px",
    }}
  >
    <div
      style={{
        width: "8px",
        height: "8px",
        backgroundColor: color,
        borderRadius: "50%",
        marginRight: "4px",
      }}
    />
    <Typography
      variant="body2"
      sx={{ fontWeight: 400, fontSize: 14, color: "gray" }}
    >
      {label}
    </Typography>
  </div>
);

interface SegmentTooltipProps {
  label: string;
  color?: string;
  bills?: number;
  pallets?: number;
  pieces?: number;
  handlingUnits?: number;
  weight: number;
  totalWeight: number;
  isSelected?: boolean;
  hasFinalDestinations?: boolean;
}
export const SegmentTooltip = ({
  label,
  color = "gray",
  bills,
  pallets,
  pieces,
  handlingUnits,
  weight,
  totalWeight,
  isSelected = false,
  hasFinalDestinations = false,
}: SegmentTooltipProps) => {
  const isHandlingUnitsEnabled = useIsFeatureFlagEnabled(
    "operations-6016-handling-units-client"
  );

  const theme = useTheme();
  const flexValue = totalWeight > 0 ? (weight / totalWeight) * 100 : 0;

  return (
    <Tooltip
      title={
        <div>
          <Typography fontSize={15}>{label}</Typography>
          {bills !== undefined && (
            <Typography variant="body1" fontSize={20}>
              {`${bills.toLocaleString()} Bills`}
            </Typography>
          )}
          {pallets !== undefined && (
            <Typography variant="body1" fontSize={20}>
              {`${pallets.toLocaleString()} Plts`}
            </Typography>
          )}
          {pieces !== undefined && (
            <Typography variant="body1" fontSize={20}>
              {`${pieces.toLocaleString()} Pcs`}
            </Typography>
          )}
          {isHandlingUnitsEnabled && handlingUnits !== undefined && (
            <Typography variant="body1" fontSize={20}>
              {`${handlingUnits.toLocaleString()} HU`}
            </Typography>
          )}
          <Typography variant="body1" fontSize={20}>
            {`${weight.toLocaleString()} lbs`}
          </Typography>
        </div>
      }
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: (theme) => theme.palette.primary.dark,
            "& .MuiTooltip-arrow": {
              color: (theme) => theme.palette.primary.dark,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          flex: `${flexValue}%`,
          backgroundColor: color,
          cursor: "pointer",
          height: "10px",
          marginTop: isSelected ? "-1px" : "0px",
          opacity: hasFinalDestinations && !isSelected ? 0.2 : 1,
          border: isSelected
            ? `2px solid ${theme.palette.common.white}`
            : "none",
          boxShadow: isSelected ? "0px 0px 4px 2px rgba(0, 0, 0, 0.3)" : "none",
          "&:hover": {
            boxShadow: "0px 0px 4px 2px rgba(0, 0, 0, 0.3)",
            border: `2px solid ${theme.palette.common.white}`,
            marginTop: "-1px",
          },
        }}
        ml={0.2}
      />
    </Tooltip>
  );
};

export function TonnageSummary({
  doors,
  loadedBills,
  notLoadedBills,
  loadedHandlingUnits,
  notLoadedHandlingUnits,
  loadedPallets,
  notLoadedPallets,
  loadedPieces,
  notLoadedPieces,
  loadedWeight,
  totalWeight,
}: TonnageSummaryProps) {
  const theme = useTheme();
  const isHandlingUnitsEnabled = useIsFeatureFlagEnabled(
    "operations-6016-handling-units-client"
  );

  const isOsadTileInLaneDetailsEnabled = useIsFeatureFlagEnabled(
    "operations-5825-OSAD-tile-in-lane-details-client"
  );

  const reconcilePercentage = useMemo(
    () =>
      loadedBills + notLoadedBills > 0
        ? ((loadedBills / (loadedBills + notLoadedBills)) * 100).toFixed(2)
        : "0.00",
    [loadedBills, notLoadedBills]
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          ml: 1,
          gap: 1,
        }}
      >
        {doors && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingLeft: 3,
            }}
            component="section"
          >
            <Typography
              variant="body2"
              component="h2"
              sx={{ fontWeight: 500, fontSize: 34, paddingRight: 1 }}
            >
              {doors}
            </Typography>
            <Typography
              variant="body2"
              component="h3"
              sx={{ fontWeight: 400, fontSize: 24, color: "gray" }}
            >
              Doors
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
          component="section"
        >
          <Typography
            variant="body2"
            component="h2"
            sx={{
              fontWeight: 500,
              fontSize: 34,
              paddingRight: 1,
            }}
          >
            {(loadedBills + notLoadedBills).toLocaleString()}
          </Typography>
          <Typography
            variant="body2"
            component="h3"
            sx={{ fontWeight: 400, fontSize: 24, color: "gray" }}
          >
            Bills
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
          component="section"
        >
          <Typography
            variant="body2"
            component="h2"
            sx={{
              fontWeight: 500,
              fontSize: 34,
              paddingRight: 1,
              paddingLeft: 3,
            }}
          >
            {(loadedPallets + notLoadedPallets).toLocaleString()}
          </Typography>
          <Typography
            variant="body2"
            component="h3"
            sx={{ fontWeight: 400, fontSize: 24, color: "gray" }}
          >
            Plts
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
          component="section"
        >
          <Typography
            variant="body2"
            component="h2"
            sx={{
              fontWeight: 500,
              fontSize: 34,
              paddingRight: 1,
              paddingLeft: 3,
            }}
          >
            {(loadedPieces + notLoadedPieces).toLocaleString()}
          </Typography>
          <Typography
            variant="body2"
            component="h3"
            sx={{ fontWeight: 400, fontSize: 24, color: "gray" }}
          >
            Pcs
          </Typography>
        </Box>
        {isHandlingUnitsEnabled && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
            component="section"
          >
            <Typography
              variant="body2"
              component="h2"
              sx={{
                fontWeight: 500,
                fontSize: 34,
                paddingRight: 1,
                paddingLeft: 3,
              }}
            >
              {(loadedHandlingUnits + notLoadedHandlingUnits).toLocaleString()}
            </Typography>
            <Typography
              variant="body2"
              component="h3"
              sx={{ fontWeight: 400, fontSize: 24, color: "gray" }}
            >
              HU
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
          component="section"
        >
          <Typography
            variant="body2"
            component="h2"
            sx={{
              fontWeight: 500,
              fontSize: 34,
              paddingRight: 1,
              paddingLeft: 3,
            }}
          >
            {totalWeight.toLocaleString()}
          </Typography>
          <Typography
            variant="body2"
            component="h3"
            sx={{ fontWeight: 400, fontSize: 24, color: "gray" }}
          >
            lbs
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Typography
            variant="body2"
            component="h2"
            sx={{
              fontWeight: 500,
              fontSize: 24,
              paddingRight: 1,
            }}
          >
            {reconcilePercentage}%
          </Typography>
          <Typography
            variant="body2"
            component="h3"
            sx={{
              fontWeight: 400,
              fontSize: 24,
              color: theme.palette.grey[600],
            }}
          >
            Reconciled
          </Typography>
        </Box>
      </Box>
      <div style={{ display: "flex", height: "9px", width: "100%" }}>
        {loadedBills === 0 ? null : (
          <SegmentTooltip
            label="Loaded"
            color={theme.palette.primary.main}
            bills={loadedBills}
            handlingUnits={loadedHandlingUnits}
            pallets={loadedPallets}
            pieces={loadedPieces}
            weight={loadedWeight}
            totalWeight={totalWeight}
          />
        )}
        <SegmentTooltip
          label="Not Loaded"
          color={alpha(theme.palette.action.active, 0.1)}
          bills={notLoadedBills}
          handlingUnits={notLoadedHandlingUnits}
          pallets={notLoadedPallets}
          pieces={notLoadedPieces}
          weight={totalWeight - loadedWeight}
          totalWeight={totalWeight}
        />
      </div>

      <Box sx={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
        <DotWithLabel color={theme.palette.primary.main} label="Loaded" />
        <DotWithLabel
          color={alpha(theme.palette.action.active, 0.1)}
          label="Not Loaded"
        />
        {isOsadTileInLaneDetailsEnabled && (
          <DotWithLabel color={theme.palette.warning.main} label="OSAD" />
        )}
      </Box>
    </>
  );
}
